import { Directive, OnDestroy } from '@angular/core';
import { environment } from 'src/environments/environment';

/**
 * Abstract base for the login and login-admin components.
 *
 * Centralises the shared infrastructure that is otherwise identical in both:
 *   - Tenant logo loading (native fetch -> blob URL -> logoSrc)
 *   - Cover image loading (native fetch -> validated blob URL -> coverBgStyle)
 *   - Blob URL lifecycle management (ngOnDestroy cleanup)
 *
 * Both methods use native fetch() intentionally — Angular's HttpClient passes
 * through KeycloakRefreshInterceptor which requires an authenticated session.
 * On the login pages the user is not yet authenticated, so the interceptor
 * would fail. fetch() bypasses the interceptor chain entirely.
 *
 * Subclasses must call this.loadAssets() inside their own ngOnInit().
 * They must NOT override ngOnDestroy() without calling super.ngOnDestroy(),
 * otherwise blob URLs will leak.
 */
// eslint-disable-next-line @angular-eslint/directive-selector
@Directive()
export abstract class LoginBaseComponent implements OnDestroy {
  /**
   * Resolved logo URL — empty until loadLogo() settles to avoid the flash
   * where the default SVG renders first and then swaps to the custom logo.
   * The img is kept invisible (opacity: 0) until logoReady flips true.
   */
  logoSrc = '';

  /** Flips true once the final src (custom or fallback) is committed. */
  logoReady = false;

  /**
   * CSS background-image value for the .login-brand__cover layer.
   * Empty until the image is fetched and validated; coverState drives
   * the CSS transition that makes it visible.
   */
  coverBgStyle = '';

  /**
   * Tracks the cover image lifecycle for template binding:
   *   'loading' — fetch in progress; shimmer skeleton is visible.
   *   'loaded'  — blob validated; cover fades in over the shimmer.
   *   'failed'  — 404, network error, or corrupt blob; fallback logo shown.
   * Starts as 'loading' so the shimmer shows immediately — no panel flash.
   */
  coverState: 'loading' | 'loaded' | 'failed' = 'loading';

  private logoBlobUrl: string | null = null;
  private coverBlobUrl: string | null = null;

  ngOnDestroy(): void {
    if (this.logoBlobUrl) {
      URL.revokeObjectURL(this.logoBlobUrl);
      this.logoBlobUrl = null;
    }
    if (this.coverBlobUrl) {
      URL.revokeObjectURL(this.coverBlobUrl);
      this.coverBlobUrl = null;
    }
  }

  /** Kick off logo + cover image fetches. Call once inside ngOnInit(). */
  protected loadAssets(): void {
    void this.loadLogo();
    void this.loadCoverImage();
  }

  /**
   * Fetches a custom image from the tenant's custom-images endpoint.
   * Adds a cache-busting query param and the X-Disable-Cache header.
   * Uses native fetch() to bypass the Keycloak interceptor.
   */
  private fetchCustomImage(path: string): Promise<Response> {
    const url = `${environment.customImagesUrl}/${path}?v=${Date.now()}`;
    // eslint-disable-next-line @typescript-eslint/naming-convention
    return fetch(url, { headers: { 'X-Disable-Cache': 'true' } });
  }

  private async loadLogo(): Promise<void> {
    try {
      const res = await this.fetchCustomImage('custom-images/logo.png');
      if (res.ok) {
        const blob = await res.blob();
        this.logoBlobUrl = URL.createObjectURL(blob);
        this.logoSrc = this.logoBlobUrl;
      } else {
        // Non-2xx (logo not configured) — fall through to default.
        this.logoSrc = 'assets/images/login/jio.svg';
      }
    } catch {
      // Network error — fall through to default.
      this.logoSrc = 'assets/images/login/jio.svg';
    }

    // Commit the resolved src in one rAF so the browser paints the correct
    // logo exactly once, with no prior flash of the default.
    requestAnimationFrame(() => {
      this.logoReady = true;
    });
  }

  /**
   * Fetches the cover image, validates it actually decodes, then transitions
   * the panel from shimmer → cover image (or shimmer → fallback on any error).
   *
   * State transitions:
   *   1. fetch error (network, CORS)     -> catch -> coverState = 'failed'
   *   2. non-2xx (404, 403, not set yet) -> coverState = 'failed'
   *   3. blob corrupt / fails to decode  -> coverState = 'failed', blob revoked
   *   4. all good -> coverBgStyle set, rAF -> coverState = 'loaded'
   *                  -> CSS transition fades cover in over the shimmer
   */
  private async loadCoverImage(): Promise<void> {
    let blobUrl: string | null = null;
    try {
      const res = await this.fetchCustomImage('custom-images/cover-image.png');
      if (!res.ok) {
        this.coverState = 'failed';
        return;
      }

      const blob = await res.blob();
      blobUrl = URL.createObjectURL(blob);

      // Validate the blob actually decodes before touching the DOM.
      // Guards against truncated/corrupt responses that arrived as 200 OK.
      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('cover image failed to decode'));
        img.src = blobUrl as string;
      });

      // Step 1: write the background-image. The shimmer is still visible —
      // no flash because coverState is still 'loading'.
      this.coverBlobUrl = blobUrl;
      this.coverBgStyle = `url(${blobUrl})`;

      // Step 2: one animation frame later, flip coverState so the CSS
      // transition fires and the cover image fades in over the shimmer.
      requestAnimationFrame(() => {
        this.coverState = 'loaded';
      });
    } catch {
      // Any failure: show fallback, revoke blob if created.
      this.coverState = 'failed';
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    }
  }
}
